import { useCallback, useContext, useState } from "react";
import { useStores } from "../../stores/root-store";
import {
  IAddOnOptionMapping,
  IProduct,
  IProductAddOnMapping,
} from "../../stores/products";
import defaultImgNa from "../../resources/images/img-default.jpg";
import { ICartItem } from "../../stores/cart";
import {
  currencyFormatter,
  getOfferDescription,
  ShowAlert,
  showConfirmationDialog,
} from "../../utils/functions";
import ProductAddOns from "./product-addons";
import { AppConfig } from "../../AppConfig";
import { AuthContext } from "../../contexts/auth-context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "react-bootstrap";
import {
  faEdit,
  faEye,
  faEyeSlash,
  faFolderPlus,
  faInfoCircle,
  faMinusCircle,
  faPercentage,
  faPlusCircle,
  faTrash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

type IProductItem = {
  product: IProduct;
  isShopClosed: boolean;
  isManageProductsRequest: boolean;
  isTableOrder?: boolean;
};

export const getShortDescription = (inputString: string, length: number) => {
  length = length ?? 50;

  if (
    inputString != undefined &&
    inputString != null &&
    inputString.length > 0
  ) {
    let newString =
      inputString.length > length
        ? inputString.substring(0, length) + "..."
        : inputString.substring(0, length);
    return newString;
  } else {
    return "";
  }
};

const ProductItem = ({
  product,
  isShopClosed,
  isManageProductsRequest,
  isTableOrder = false,
}: IProductItem) => {
  const { cartStore, productStore } = useStores();
  const { addItemToCart } = cartStore;
  const { setEditProduct, hideProduct, deleteProduct, fetchProducts } =
    productStore;
  const [itemQty, setItemQty] = useState(1);
  const [isAddOnsModalOpen, setIsAddOnsModalOpen] = useState(false);
  const [showAllergens, setShowAllergens] = useState(false);
  const [showOfferTitle, setShowOfferTitle] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const hasAddOns = product.productAddOns?.length;

  const handleAddToCart = (item: ICartItem) => {
    if (isShopClosed) {
      ShowAlert.Error("Sorry, we are not taking orders at the moment.");
      return;
    }

    addItemToCart(item, itemQty);
    setItemQty(1);
    setIsAddOnsModalOpen(false);
  };

  const setAddOnsModelVisibility = useCallback(
    (show: boolean) => {
      setIsAddOnsModalOpen(show);
      if (show) {
        product.productAddOns?.map(({ addOn }: IProductAddOnMapping) => {
          addOn.addOnOptions.map(({ addOnOption }: IAddOnOptionMapping) => {
            addOnOption.choosen = false;
          });
        });
      }
    },
    [isAddOnsModalOpen]
  );

  const handleHide = (productId: number, hide: boolean) => {
    hideProduct(productId, hide, (res: boolean) => {
      if (res) {
        fetchProducts();
        ShowAlert.Success(
          hide
            ? "Product hidden for customers."
            : "Product enabled and visible for customers"
        );
      } else ShowAlert.Error("Request failed. Please try again.");
    });
  };

  const handleDelete = (productId: number) => {
    showConfirmationDialog(() => {
      deleteProduct(productId, (res: boolean) => {
        if (res) {
          fetchProducts();
          ShowAlert.Success("Product has deleted successfully.");
        } else ShowAlert.Error("Request failed. Please try again.");
      });
    });
  };

  if (isTableOrder && !isManageProductsRequest) {
    const currentPrice =
      product.discountForQuantity === 1 && product.discountPrice
        ? product.discountPrice
        : product.sellPrice;

    return (
      <>
        <article className="card table-product-card h-100">
          <div className="card-body table-product-summary">
            <button
              type="button"
              className="btn table-product-info"
              aria-label={`View details for ${product.productName}`}
              onClick={() => setShowDetails(true)}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </button>
            <h6 className="table-product-name">{product.productName}</h6>
            <strong className="table-product-price">
              {currencyFormatter(currentPrice)}
            </strong>
          </div>
          <div className="table-product-actions">
            {!hasAddOns && (
              <div className="table-quantity-control" aria-label="Quantity">
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  onClick={() => setItemQty(itemQty > 1 ? itemQty - 1 : 1)}
                >
                  <FontAwesomeIcon icon={faMinusCircle} />
                </button>
                <strong>{itemQty}</strong>
                <button
                  type="button"
                  aria-label="Increase quantity"
                  onClick={() => setItemQty(itemQty + 1)}
                >
                  <FontAwesomeIcon icon={faPlusCircle} />
                </button>
              </div>
            )}
            <button
              type="button"
              className="btn btn-default table-add-button"
              onClick={() =>
                hasAddOns
                  ? setAddOnsModelVisibility(true)
                  : handleAddToCart(product as ICartItem)
              }
            >
              <FontAwesomeIcon icon={faPlusCircle} />
              <span>{hasAddOns ? "Customise" : "Add"}</span>
            </button>
          </div>
        </article>

        <Modal
          show={showDetails}
          onHide={() => setShowDetails(false)}
          centered
          dialogClassName="table-product-details-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>{product.productName}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <img
              src={
                product.productImagePath
                  ? `${AppConfig.APIBaseUrl}${product.productImagePath}`
                  : defaultImgNa
              }
              alt={product.productName}
              className="table-product-detail-image"
            />
            {product.description && <p className="mt-3">{product.description}</p>}
            {(product.allergens?.length ?? 0) > 0 && (
              <div className="alert alert-warning py-2">
                <strong>Allergens:</strong>{" "}
                {product.allergens
                  ?.map((item) => item.allergen?.allergenName)
                  .join(", ")}
              </div>
            )}
            {product.offers?.map((offer, index) => (
              <div key={index} className="text-success fw-bold mb-2">
                {getOfferDescription(offer, true)}
              </div>
            ))}
            <div className="d-flex justify-content-between align-items-center fs-5 fw-bold">
              <span>Price</span>
              <span>{currencyFormatter(currentPrice)}</span>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-default w-100"
              onClick={() => {
                setShowDetails(false);
                if (hasAddOns) setAddOnsModelVisibility(true);
                else handleAddToCart(product as ICartItem);
              }}
            >
              <FontAwesomeIcon icon={faPlusCircle} className="me-2" />
              {hasAddOns ? "Customise and add" : "Add to order"}
            </button>
          </Modal.Footer>
        </Modal>

        {hasAddOns ? (
          <ProductAddOns
            cartItem={{ ...product } as ICartItem}
            isModalOpen={isAddOnsModalOpen}
            onModalClose={() => setAddOnsModelVisibility(false)}
            addProductToCart={handleAddToCart}
            isEditRequest={false}
            itemQty={itemQty}
            setItemQty={setItemQty}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <div className="card product-card shadow-lg bg-body rounded min-height-100 mb-3">
        <img
          src={
            product?.productImagePath
              ? `${AppConfig.APIBaseUrl}${product.productImagePath}`
              : defaultImgNa
          }
          alt="Product image"
          className="p-2 product-image card-img-top"
        />
        {product.offers && product.offers.length > 0 && (
          <span
            className="position-absolute translate-middle badge rounded bg-success badge-offer"
            onMouseOut={() => {
              setShowOfferTitle(false);
            }}
            onMouseOver={() => {
              setShowOfferTitle(true);
            }}
          >
            <FontAwesomeIcon className="fs-4" icon={faPercentage} />
            {showOfferTitle && (
              <div className="position-absolute end-0">
                <div className="card bg-success text-white">
                  <div className="card-body">
                    {product.offers.map((p) => (
                      <div>{getOfferDescription(p, true)}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </span>
        )}
        <div className="card-body p-3 text-start">
          <h6 className="card-title">
            {getShortDescription(product.productName, 45)}
            {product.allergens && product.allergens.length > 0 && (
              <>
                <i
                  className="float-end cursor-pointer"
                  onMouseOut={() => {
                    setShowAllergens(false);
                  }}
                  onMouseOver={() => {
                    setShowAllergens(true);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="text-warning fa-2x"
                  ></FontAwesomeIcon>
                  {showAllergens && (
                    <div className="position-absolute" style={{ zIndex: 999 }}>
                      <div className="card">
                        <div className="card-body">
                          {product.allergens
                            .map((p) => p.allergen?.allergenName)
                            .join(", ")}
                        </div>
                      </div>
                    </div>
                  )}
                </i>
              </>
            )}
          </h6>
          {product.description && (
            <div className="card-subtitle mb-2 text-muted small">
              {product.description}
            </div>
          )}

          <div className="card-text">
            <div className="product-price">
              <span className="new-price fs-6">
                {hasAddOns ? (
                  <small className="me-1 text-muted">
                    Customisation available from
                  </small>
                ) : (
                  ""
                )}
                {}
                {product.discountForQuantity === 1 && product.discountPrice ? (
                  <>
                    <del>{currencyFormatter(product.sellPrice)}</del>
                    <span className="bg-default badge ms-2 fs-6">
                      {currencyFormatter(product.discountPrice)}
                    </span>
                  </>
                ) : (
                  currencyFormatter(product.sellPrice)
                )}
              </span>
              {product.offers &&
                product.offers.length > 0 &&
                product.offers.map((p) => (
                  <span className="text-success">
                    {getOfferDescription(p, true)}
                  </span>
                ))}
            </div>
          </div>
        </div>
        {isManageProductsRequest ? (
          <div className="d-flex justify-content-between align-items-center p-3">
            <FontAwesomeIcon
              icon={faEdit}
              className="text-default fs-4 cursor-pointer"
              onClick={() => setEditProduct(product, false)}
            />
            <span className="position-relative">
              <FontAwesomeIcon
                icon={faFolderPlus}
                className="text-primary fs-4 cursor-pointer"
                onClick={() => setEditProduct(product, true)}
              ></FontAwesomeIcon>

              {product.productAddOns != null &&
                product.productAddOns.length > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {product.productAddOns?.length}
                  </span>
                )}
            </span>
            {product.hidden ? (
              <FontAwesomeIcon
                onClick={() => handleHide(product.productId, false)}
                icon={faEye}
                className="text-success fs-4 cursor-pointer"
              />
            ) : (
              <FontAwesomeIcon
                icon={faEyeSlash}
                className="text-secondary fs-4 cursor-pointer"
                onClick={() => handleHide(product.productId, true)}
              />
            )}
            <FontAwesomeIcon
              icon={faTrash}
              className="text-danger fs-4 cursor-pointer"
              onClick={() => handleDelete(product.productId)}
            />
          </div>
        ) : (
          !isShopClosed && (
            <div className="d-flex justify-content-between align-items-center p-1 mb-1">
              {!hasAddOns && (
                <div className="me-2 ms-2">
                  <FontAwesomeIcon
                    icon={faMinusCircle}
                    className="text-danger cursor-pointer"
                    onClick={() => setItemQty(itemQty > 1 ? itemQty - 1 : 1)}
                  />

                  <span className="h6 p-2">{itemQty}</span>

                  <FontAwesomeIcon
                    icon={faPlusCircle}
                    className="text-success cursor-pointer"
                    onClick={() => setItemQty(itemQty + 1)}
                  />
                </div>
              )}
              <button
                className={`btn btn-default flex-fill`}
                onClick={() => {
                  hasAddOns
                    ? setAddOnsModelVisibility(true)
                    : handleAddToCart(product as ICartItem);
                }}
              >
                <FontAwesomeIcon icon={faPlusCircle} className="me-1" />
                {hasAddOns
                  ? "Add from " + currencyFormatter(product.sellPrice)
                  : "Add"}
              </button>
            </div>
          )
        )}
      </div>
      {hasAddOns ? (
        <ProductAddOns
          cartItem={{ ...product } as ICartItem}
          isModalOpen={isAddOnsModalOpen}
          onModalClose={() => {
            setAddOnsModelVisibility(false);
          }}
          addProductToCart={handleAddToCart}
          isEditRequest={false}
          itemQty={itemQty}
          setItemQty={setItemQty}
        />
      ) : null}
    </>
  );
};

export default ProductItem;
